import React from "react";
import { Link } from "react-router-dom";
import UserBullet from "../../UserBullet";
import { createMarkup } from "../../../helpers/generic";

const SAMPLE = [
    { id: 1, title: "Login page broken on mobile",      description: "Users on iOS 17 report a white screen after submitting credentials. Happens only on Safari.", updated_at: new Date(Date.now() - 4   * 60000).toISOString(),    status: { name: "Open",    color: "#3b82f6" }, department: { name: "Support",     color: "#8b5cf6" }, priority: { name: "High",   color: "#ef4444" }, assignee: { name: "Alice M.",  profile_image: null } },
    { id: 2, title: "Invoice not generating correctly", description: "PDF invoices are missing line items when there are more than 10 products in the order.",          updated_at: new Date(Date.now() - 18  * 60000).toISOString(),   status: { name: "Active",  color: "#8b5cf6" }, department: { name: "Billing",     color: "#f59e0b" }, priority: { name: "Medium", color: "#f59e0b" }, assignee: { name: "Bob K.",    profile_image: null } },
    { id: 3, title: "Email notifications delayed",      description: "Notification emails are arriving 2–3 hours late. Affects password resets and ticket updates.",    updated_at: new Date(Date.now() - 2   * 3600000).toISOString(),  status: { name: "Closed",  color: "#22c55e" }, department: { name: "Engineering", color: "#06b6d4" }, priority: { name: "Low",    color: "#22c55e" }, assignee: null },
    { id: 4, title: "Dark mode flickering issue",       description: "Theme flickers between light and dark on page load. Reproducible on Chrome 123 and Firefox.",     updated_at: new Date(Date.now() - 5   * 3600000).toISOString(),  status: { name: "Pending", color: "#f59e0b" }, department: { name: "Support",     color: "#8b5cf6" }, priority: { name: "High",   color: "#ef4444" }, assignee: { name: "Carol S.",  profile_image: null } },
    { id: 5, title: "CSV export missing columns",       description: "Exported CSV files do not include the custom fields added in the last release.",                   updated_at: new Date(Date.now() - 86400000).toISOString(),       status: { name: "Open",    color: "#3b82f6" }, department: { name: "Engineering", color: "#06b6d4" }, priority: { name: "Medium", color: "#f59e0b" }, assignee: { name: "David P.",  profile_image: null } },
    { id: 6, title: "2FA setup wizard crashes on iOS",  description: "The setup wizard for two-factor authentication throws an unhandled exception on step 3.",        updated_at: new Date(Date.now() - 2   * 86400000).toISOString(), status: { name: "Active",  color: "#8b5cf6" }, department: { name: "Security",    color: "#ef4444" }, priority: { name: "High",   color: "#ef4444" }, assignee: { name: "Elena R.",  profile_image: null } },
];

const formatTime = (dateString) => {
    const date      = new Date(dateString);
    const now       = new Date();
    const diffMs    = now - date;
    const diffMins  = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays  = Math.floor(diffMs / 86400000);
    if (diffMins  < 1)  return "just now";
    if (diffMins  < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays  < 7)  return `${diffDays}d ago`;
    return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

const Chip = ({ color, text }) => (
    <span style={{
        display:        "inline-flex",
        alignItems:     "center",
        gap:            4,
        fontSize:       "0.68rem",
        fontWeight:     500,
        padding:        "2px 7px",
        borderRadius:   5,
        background:     "#f0f0f0",
        color:          "#333",
        whiteSpace:     "nowrap",
        border:         "1px solid #e0e0e0",
    }}>
        <span style={{ width: 5, height: 5, borderRadius: "50%", background: color, flexShrink: 0 }} />
        {text}
    </span>
);

const LastUpdatedTickets = ({ tickets = [], useSampleData }) => {
    const data = useSampleData ? SAMPLE : tickets;

    return (
        <div className="header-panel-dashboard panelContainer bg-white p-4" style={{ borderRadius: 10, display: "flex", flexDirection: "column", height: "100%" }}>

            {/* ── Header ── */}
            {/* <div className="mb-3" style={{ flexShrink: 0 }}>
                <h6 style={{ fontWeight: 700, color: '#111827', marginBottom: 2 }}>Last Updated Tickets</h6>
                <small style={{ color: '#6B7280' }}>Most recently modified</small>
            </div> */}

            {/* ── Scrollable grid ── */}
            <div style={{ flex: 1, overflowY: "auto" }}>
                {data.length === 0 ? (
                    <p style={{ fontSize: "0.82rem", color: "#9ca3af", textAlign: "center", margin: "20px 0" }}>
                        No tickets found.
                    </p>
                ) : (
                    <div style={{
                        display:               "grid",
                        gridTemplateColumns:   "repeat(auto-fill, minmax(240px, 1fr))",
                        gap:                   10,
                        paddingBottom:         20,
                    }}>
                        {data.map(ticket => (
                            <Link
                                key={ticket.id}
                                to={`/ticket/${ticket.id}`}
                                style={{ textDecoration: "none", color: "inherit" }}
                            >
                                <div style={{
                                    border:        "1.5px solid #e5e7eb",
                                    borderRadius:  8,
                                    padding:       "12px 13px",
                                    display:       "flex",
                                    flexDirection: "column",
                                    gap:           9,
                                    background:    "#fff",
                                    transition:    "border-color 0.15s",
                                    cursor:        "pointer",
                                    height:        "100%",
                                }}
                                    onMouseEnter={e => e.currentTarget.style.borderColor = "#a5b4fc"}
                                    onMouseLeave={e => e.currentTarget.style.borderColor = "#e5e7eb"}
                                >
                                    {/* Title */}
                                    <div style={{
                                        fontSize:        "0.83rem",
                                        fontWeight:      600,
                                        textAlign:       "left",
                                        color:           "#111827",
                                        lineHeight:      1.4,
                                        display:         "-webkit-box",
                                        WebkitLineClamp: 2,
                                        WebkitBoxOrient: "vertical",
                                        overflow:        "hidden",
                                    }}>
                                        {ticket.title}
                                    </div>

                                    {/* Description */}
                                    {ticket.description && (
                                        <div
                                            style={{
                                                fontSize:        "0.75rem",
                                                textAlign:       "left",
                                                color:           "#6b7280",
                                                lineHeight:      1.5,
                                                display:         "-webkit-box",
                                                WebkitLineClamp: 2,
                                                WebkitBoxOrient: "vertical",
                                                overflow:        "hidden",
                                            }}
                                            dangerouslySetInnerHTML={createMarkup(ticket.description)}
                                        />
                                    )}

                                    {/* Chips: status, priority, department */}
                                    <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                                        {ticket.status     && <Chip color={ticket.status.color}     text={ticket.status.name} />}
                                        {ticket.priority   && <Chip color={ticket.priority.color}   text={ticket.priority.name} />}
                                        {ticket.department && <Chip color={ticket.department.color} text={ticket.department.name} />}
                                    </div>

                                    {/* Footer: avatar(s) + time */}
                                    <div style={{
                                        display:        "flex",
                                        alignItems:     "center",
                                        justifyContent: "space-between",
                                        marginTop:      "auto",
                                        paddingTop:     4,
                                        borderTop:      "1px solid #f3f4f6",
                                    }}>
                                        {(() => {
                                            const assignees = ticket.assignees?.length
                                                ? ticket.assignees
                                                : ticket.assignee
                                                    ? [ticket.assignee]
                                                    : [];
                                            if (!assignees.length) {
                                                return <span style={{ fontSize: "0.72rem", color: "#d1d5db" }}>Unassigned</span>;
                                            }
                                            return (
                                                <div style={{ display: "flex", alignItems: "center" }}>
                                                    {assignees.slice(0, 4).map((u, idx) => (
                                                        <div key={idx} style={{ marginLeft: idx === 0 ? 0 : 3, zIndex: assignees.length - idx }}>
                                                            <UserBullet user={u} size={22} tooltip={true} />
                                                        </div>
                                                    ))}
                                                    {assignees.length > 4 && (
                                                        <span style={{
                                                            marginLeft:   3,
                                                            width:        22,
                                                            height:       22,
                                                            borderRadius: "50%",
                                                            background:   "#e5e7eb",
                                                            border:       "1.5px solid #fff",
                                                            display:      "flex",
                                                            alignItems:   "center",
                                                            justifyContent: "center",
                                                            fontSize:     "0.6rem",
                                                            fontWeight:   600,
                                                            color:        "#6b7280",
                                                        }}>
                                                            +{assignees.length - 4}
                                                        </span>
                                                    )}
                                                </div>
                                            );
                                        })()}
                                        <span style={{ fontSize: "0.7rem", color: "#9ca3af", whiteSpace: "nowrap" }}>
                                            {formatTime(ticket.updated_at)}
                                        </span>
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default LastUpdatedTickets;
